import axios from "axios";
import { useEffect, useState } from "react";
import { Button } from "./DanitComponents/Buttons/Button";
import InputWithLabel from "./DanitComponents/Inputs/InputWithLabel";
import { baseUrl } from "./assets/Animations";
import LoginBgTeacher from "./static/img/login-background-teacher.jpg";
import { displayVersionNumber } from "./utilityFunctions";
import {
  getPasswordResetErrorMessage,
  isValidPortalEmail,
  normalizePortalEmail,
} from "./loginUtils";

const Login = ({ accessPoint, onLoggedIn }) => {
  const [schoolInfo, setSchoolInfo] = useState({
    abbreviation: "",
    schoolAbb: "",
    schoolName: "",
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetStatus, setResetStatus] = useState("idle");
  const [resetMessage, setResetMessage] = useState("");

  useEffect(() => {
    let url = window.location.href;
    let trimmed = url.substring(8);
    let name = trimmed.split(".")[0];
    if (name === "mountainside") {
      setSchoolInfo({
        abbreviation: "MMA",
        schoolAbb: "MMA",
        schoolName: "Mountainside",
        logoUrl: "https://www.mountainsidemusicacademy.com/logo.png",
      });
    } else if (name === "lakeside") {
      setSchoolInfo({
        abbreviation: "LSMA",
        schoolAbb: "LSMA",
        schoolName: "Lakeside",
        logoUrl: "https://www.lakesidemusicacademy.com/logo.png",
      });
    } else if (name === "treeside") {
      setSchoolInfo({
        abbreviation: "TSMA",
        schoolAbb: "TMA",
        schoolName: "Treeside",
        logoUrl: "https://www.treesidemusicacademy.com/logo.png",
      });
    } else if (name === "morningside") {
      setSchoolInfo({
        abbreviation: "MMA",
        schoolAbb: "MMA",
        schoolName: "Morningside",
        logoUrl: "https://www.morningsidemusicacademy.com/logo.png",
      });
    } else if (name === "parkside") {
      setSchoolInfo({
        abbreviation: "PSMA",
        schoolAbb: "PSMA",
        schoolName: "Parkside",
        logoUrl: "https://www.parksidemusicacademy.com/logo.png",
      });
    } else if (name === "denton") {
      setSchoolInfo({
        abbreviation: "DEN",
        schoolAbb: "DEN",
        schoolName: "Denton",
        logoUrl: "https://www.atharomusic.com/denton-logo__dark.png",
      });
    } else if (name === "tempe") {
      setSchoolInfo({
        abbreviation: "TEM",
        schoolAbb: "TEM",
        schoolName: "Tempe",
        logoUrl: "https://www.atharomusic.com/tempe-logo__dark.png",
      });
    } else if (name === "tulsa") {
      setSchoolInfo({
        abbreviation: "TUL",
        schoolAbb: "TUL",
        schoolName: "Tulsa",
        logoUrl: "https://www.atharomusic.com/tulsa-logo__dark.png",
      });
    }
  }, []);

  const adminSubmit = async () => {
    const preparedEmail = normalizePortalEmail(email);
    axios
      .post(`${baseUrl}/login`, {
        Email: preparedEmail,
        Password: password,
      })
      .then((response) => {
        if (response.data.user.School === "admin") {
          onLoggedIn(response.data);
        } else {
          alert("Access not granted");
        }
      })
      .catch((e) => {
        console.log(e);
        alert("Incorrect Username or Password. Please try again...");
      });
  };

  const teacherSubmit = async () => {
    const preparedEmail = normalizePortalEmail(email);
    axios
      .post(`${baseUrl}/login`, {
        Email: preparedEmail,
        Password: password,
      })
      .then((response) => {
        onLoggedIn(response.data);
      })
      .catch((e) => {
        console.log("no such user " + e);
        console.log(e);
        alert("Incorrect Username or Password. Please try again...");
      });
  };

  const openReset = () => {
    setShowReset(true);
    setPassword("");
    setResetStatus("idle");
    setResetMessage("");
  };

  const returnToLogin = () => {
    setShowReset(false);
    setPassword("");
    setResetStatus("idle");
    setResetMessage("");
  };

  const resetPass = async () => {
    if (resetStatus === "loading") return;

    const preparedEmail = normalizePortalEmail(email);
    setEmail(preparedEmail);

    if (!isValidPortalEmail(preparedEmail)) {
      setResetStatus("error");
      setResetMessage("Enter the complete email address used for your teacher profile.");
      return;
    }

    setResetStatus("loading");
    setResetMessage("");

    try {
      await axios.put(`${baseUrl}/fullpassreset`, { Email: preparedEmail });
      setResetStatus("success");
      setResetMessage(
        `A temporary password was sent to ${preparedEmail}. Check your inbox and spam folder before requesting another one.`
      );
    } catch (error) {
      console.error("Password reset failed:", error?.response?.data || error?.message);
      setResetStatus("error");
      setResetMessage(getPasswordResetErrorMessage(error));
    }
  };

  const isAdmin = accessPoint === "admin";
  const title = isAdmin ? "Admin Portal" : (showReset ? "Reset Password" : "Teacher Portal");
  const bgImage = isAdmin
    ? "./login-background.jpg"
    : schoolInfo.schoolAbb
    ? `./${schoolInfo.schoolAbb}.jpg`
    : LoginBgTeacher;

  const onSubmit = () => {
    if (isAdmin) adminSubmit();
    else teacherSubmit();
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (showReset) resetPass();
    else onSubmit();
  };

  return (
    <div
      className="w-screen h-screen flex items-center justify-center bg-cover"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700 m-5">
        <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
          <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
            {title}
          </h1>
          <form className="space-y-4 md:space-y-6" onSubmit={handleFormSubmit} noValidate>
            {showReset && resetStatus !== "success" && (
              <div className="text-sm leading-6 text-slate-600">
                Enter the email used for your teacher profile. We’ll send one temporary password,
                which you’ll replace after signing in.
              </div>
            )}
            {resetStatus !== "success" && (
              <InputWithLabel
                label="Email"
                name="email"
                type="email"
                placeholder="teacher@example.com"
                onChange={(value) => {
                  setEmail(value);
                  if (showReset && resetStatus === "error") {
                    setResetStatus("idle");
                    setResetMessage("");
                  }
                }}
                value={email}
                inputProps={{ autoComplete: "email", autoCapitalize: "none", spellCheck: false }}
              />
            )}
            {!showReset && (
              <InputWithLabel
                label="Password"
                name="password"
                type="password"
                placeholder="Password"
                onChange={(value) => setPassword(value)}
                value={password}
                inputProps={{ autoComplete: "current-password" }}
              />
            )}
            <div className="flex items-center justify-end">
              {!isAdmin && !showReset && (
                <button
                  type="button"
                  className="bg-transparent text-blue-700 hover:text-blue-300 font-semibold py-2 px-4 border border-transparent small text-sm"
                  onClick={openReset}
                >
                  Forgot my password?
                </button>
              )}
            </div>
            {resetMessage && (
              <div
                role={resetStatus === "error" ? "alert" : "status"}
                className={`rounded-md border px-4 py-3 text-sm leading-5 ${
                  resetStatus === "success"
                    ? "border-green-200 bg-green-50 text-green-800"
                    : "border-red-200 bg-red-50 text-red-800"
                }`}
              >
                {resetMessage}
              </div>
            )}
            {resetStatus !== "success" && (
              <Button
                color={showReset ? "warning" : "primary"}
                text={
                  showReset
                    ? resetStatus === "loading"
                      ? "Sending temporary password…"
                      : "Email temporary password"
                    : "Log in"
                }
                type="submit"
                isDisabled={resetStatus === "loading"}
                size="lg"
                fullWidth
              />
            )}
            {showReset && (
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  className="text-sm font-semibold text-orange-700 underline hover:text-orange-900"
                  onClick={returnToLogin}
                >
                  {resetStatus === "success" ? "Back to log in" : "Cancel"}
                </button>
              </div>
            )}
            <div className="flex items-center justify-center mt-4">
              <div className="text-slate-400 text-xs -mb-7 font-extrabold">{displayVersionNumber()}</div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
